import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Close from "@mui/icons-material/Close";
import Download from "@mui/icons-material/Download";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { testIds } from "./ImagePreviewDialog.testIds";

export type ImagePreviewDialogProps = {
  open: boolean;
  images: string[];
  initialIndex?: number;
  onClose: () => void;
};

export default function ImagePreviewDialog({
  open,
  images,
  initialIndex = 0,
  onClose,
}: ImagePreviewDialogProps) {
  const { t } = useTranslation("common");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (open && images.length) {
      const safe = Math.min(
        Math.max(0, initialIndex),
        Math.max(0, images.length - 1)
      );
      setActiveIndex(safe);
    }
  }, [open, initialIndex, images.length]);

  const currentUrl = images[activeIndex] ?? "";

  const goPrev = useCallback(() => {
    setActiveIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  }, [images.length]);

  const goNext = useCallback(() => {
    setActiveIndex((i) => (i < images.length - 1 ? i + 1 : 0));
  }, [images.length]);

  if (!images.length) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="image-preview-dialog-title"
    >
      <DialogTitle
        id="image-preview-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6">
          {t("documents.preview", "Preview")}
          {images.length > 1
            ? ` (${activeIndex + 1} / ${images.length})`
            : null}
        </Typography>
        <IconButton data-testid={testIds.imagePreviewDialogClose} aria-label="close" onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          bgcolor: "action.hover",
          position: "relative",
        }}
      >
        {images.length > 1 ? (
          <IconButton data-testid={testIds.imagePreviewDialogPreviousImage}
            aria-label="previous image"
            onClick={goPrev}
            sx={{
              position: "absolute",
              left: 8,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "background.paper" },
            }}
            size="large"
          >
            <ChevronLeft fontSize="large" />
          </IconButton>
        ) : null}
        <Box
          component="img"
          src={currentUrl}
          alt=""
          sx={{
            maxWidth: "100%",
            maxHeight: "70vh",
            objectFit: "contain",
            display: "block",
          }}
        />
        {images.length > 1 ? (
          <IconButton data-testid={testIds.imagePreviewDialogNextImage}
            aria-label="next image"
            onClick={goNext}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "background.paper" },
            }}
            size="large"
          >
            <ChevronRight fontSize="large" />
          </IconButton>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1} sx={{ px: 1, pb: 1 }}>
          <IconButton data-testid={testIds.imagePreviewDialogDownloadImage}
            component="a"
            color="primary"
            href={currentUrl || undefined}
            download={`issue-photo-${activeIndex + 1}.jpg`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("documents.download", "Download")}
            disabled={!currentUrl}
            size="medium"
          >
            <Download />
          </IconButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
